import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Phone, MapPin, Wrench, LogOut, RefreshCw, Inbox } from "lucide-react";
import { API } from "@/constants/site";

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("gta_admin_key") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (k) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": k } });
        setLeads(data.leads);
        localStorage.setItem("gta_admin_key", k);
        setKey(k);
      } catch {
        setError("Invalid passcode. Try again.");
        setLeads(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.removeItem("gta_admin_key");
    setKey("");
    setLeads(null);
  };

  if (!key || !leads) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-dark px-4" data-testid="admin-login">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(input);
          }}
          className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl"
        >
          <img src="/logo.jpg" alt="GTA Appliance Rescue" className="mx-auto h-14 w-auto" />
          <h1 className="mt-6 text-center font-display text-xl font-bold text-navy">Leads Dashboard</h1>
          <p className="mt-1 text-center text-xs text-slate-500">Enter your admin passcode to view service requests.</p>
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin passcode"
            className="mt-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-rescue"
          />
          {error && (
            <p className="mt-2 text-center text-xs font-semibold text-rescue" data-testid="admin-error">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-4 w-full rounded-full bg-rescue py-3 text-sm font-bold text-white transition-colors hover:bg-rescue-dark disabled:opacity-60"
          >
            {loading ? "Checking…" : "View Leads"}
          </button>
          <a href="/" className="mt-4 block text-center text-xs text-slate-400 hover:text-slate-600">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="GTA Appliance Rescue" className="h-10 w-auto" />
            <div>
              <h1 className="font-display text-lg font-bold text-navy">Service Request Leads</h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                {leads.length} total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-button"
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy-dark"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {leads.length === 0 ? (
          <div className="flex flex-col items-center rounded-3xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="leads-empty">
            <Inbox className="h-12 w-12 text-slate-300" />
            <p className="mt-4 font-display text-lg font-bold text-navy">No leads yet</p>
            <p className="mt-1 text-sm text-slate-500">New service requests will appear here in real time.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((lead) => (
              <article
                key={lead.lead_id}
                data-testid={`lead-card-${lead.lead_id}`}
                className="rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-lg hover:shadow-slate-900/5 sm:p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="font-display text-lg font-bold text-navy">{lead.name}</h2>
                      <span className="rounded-full bg-red-50 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-rescue">
                        #{lead.lead_id}
                      </span>
                    </div>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                      <MapPin className="h-3.5 w-3.5" /> {lead.address}
                    </p>
                  </div>
                  <a
                    href={`tel:${lead.phone}`}
                    className="flex items-center gap-2 rounded-full bg-rescue px-4 py-2 text-xs font-bold text-white hover:bg-rescue-dark"
                  >
                    <Phone className="h-3.5 w-3.5" /> {lead.phone}
                  </a>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white">
                    <Wrench className="h-3 w-3" /> {lead.appliance_type}
                  </span>
                  {lead.brand && (
                    <span className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">
                      {lead.brand}
                    </span>
                  )}
                  <span className="rounded-full border border-slate-200 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                    {new Date(lead.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <p className="mt-3 rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">
                  {lead.problem}
                </p>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
